/* Copyright (c) 2020-present CloudIO Inc */

import { IconButton, InputAdornment, TextField, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import type { IconValue, ThemeColor } from 'componentregistry';
import React from 'react';
import { getBGColor, getColor } from 'theme/useThemeColor';
import Icon from './Icon';
import IconPickerDialog from './IconPickerDialog';

type IconStyleProps = {
  parentColor?: string;
  iconColor?: string;
};

const getIconColor = (
  theme: Theme,
  { parentColor, iconColor }: IconStyleProps,
) => {
  return parentColor
    ? getColor(theme, parentColor)
    : getBGColor(theme, iconColor);
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      position: 'relative',
      cursor: 'pointer',
    },
    flex: {
      display: 'flex',
    },
    flexIcon: {
      margin: 4,
      marginRight: 12,
    },
    icon: ({ parentColor }: { parentColor?: string }) => ({
      backgroundColor: getBGColor(theme, parentColor),
    }),
    iconFill: ({ parentColor, iconColor }: IconStyleProps) => ({
      fill: getIconColor(theme, { parentColor, iconColor }),
      stroke: getIconColor(theme, { parentColor, iconColor }),
    }),
    field: {
      cursor: 'pointer',
      '& input': {
        cursor: 'pointer',
      },
    },
  };
});

type Props = {
  value?: IconValue;
  onChange: (value: IconValue) => void;
  placeholder?: string;
  label?: string;
  helpText?: string;
  parentColor?: ThemeColor;
  color?: ThemeColor;
  className?: string;
  'data-testid': string;
  disabled?: boolean;
};

export const IconChooser = ({
  value,
  onChange,
  placeholder,
  label,
  helpText,
  parentColor,
  className,
  'data-testid': dataTestId,
  disabled = false,
}: Props): React.ReactElement => {
  const classes = useStyles({ parentColor, iconColor: value?.color });
  const [open, setOpen] = React.useState(false);

  const startAdornment = (
    <>
      {value?.iconName ? (
        <InputAdornment position="start">
          <Icon
            disabled={disabled}
            iconName={value.iconName}
            iconType={value.iconType}
          />
        </InputAdornment>
      ) : (
        <IconButton
          disabled={disabled}
          data-testid={`${dataTestId}-search`}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          <Icon disabled={disabled} iconName="search" />
        </IconButton>
      )}
    </>
  );

  const endAdornment = (
    <IconButton
      data-testid={`${dataTestId}-clear`}
      disabled={disabled}
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(false);
        onChange({ iconName: '' });
      }}
    >
      <span aria-hidden="false" style={{ display: 'none' }}>
        Close
      </span>
      <Icon disabled={disabled} iconName="times" />
    </IconButton>
  );

  return (
    <div className={clsx(className, classes.root)}>
      <TextField
        disabled={disabled}
        className={classes.field}
        value={
          value?.iconName
            ? `${value.iconType || 'light'}:${value.iconName}`
            : ''
        }
        helperText={helpText}
        label={label}
        placeholder={placeholder}
        InputProps={{
          startAdornment,
          endAdornment,
        }}
        onChange={(e) => {
          e.preventDefault();
        }}
        variant="standard"
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        inputProps={{
          'data-testid': `${dataTestId}-input`,
          'data-clickable': true,
        }}
      />
      {open ? (
        <IconPickerDialog
          open={open}
          onChange={(icon) => {
            setOpen(false);
            onChange(icon);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
};
